'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useAuthStore } from '@/app/lib/store/authStore'
import { isAdmin } from '@/app/lib/types/auth'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface NavLink {
  label: string
  href: string
  /** When true the link opens as a plain anchor (e.g. mailto:) */
  external?: boolean
}

/* ------------------------------------------------------------------ */
/*  Static data                                                        */
/* ------------------------------------------------------------------ */

const NAV_LINKS: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'New Videos', href: '/new-videos' },
  { label: 'Resources I Use', href: '/resources' },
  { label: 'Files', href: '/files' },
  { label: 'Favorites', href: '/favorites' },
  { label: 'Help', href: 'mailto:support@deeppockets.com', external: true },
]

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function NavBar() {
  const router = useRouter()
  const pathname = usePathname()

  const { isAuthenticated, user, logout } = useAuthStore()
  const userIsAdmin = isAdmin(user)

  /* ---- local state ---- */
  const [scrolled, setScrolled] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  /* ---- refs ---- */
  const userMenuRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  /* ---- scroll listener ---- */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /* ---- close mobile menu on route change ---- */
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  /* ---- dismiss menus on Escape key ---- */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false)
        setMobileMenuOpen(false)
        setSearchOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  /* ---- close user dropdown on outside click ---- */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /* ---- focus search input when opened ---- */
  useEffect(() => {
    if (searchOpen) {
      // Small delay to let the width animation start so the input is visible
      const id = setTimeout(() => searchInputRef.current?.focus(), 120)
      return () => clearTimeout(id)
    }
  }, [searchOpen])

  /* ---- handlers ---- */
  const handleLogout = useCallback(async () => {
    await logout()
    setUserMenuOpen(false)
    router.push('/login')
  }, [logout, router])

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const q = searchQuery.trim()
      if (q) {
        router.push(`/search?q=${encodeURIComponent(q)}`)
        setSearchOpen(false)
        setSearchQuery('')
      }
    },
    [searchQuery, router],
  )

  const toggleSearch = useCallback(() => {
    setSearchOpen((prev) => !prev)
    if (searchOpen) setSearchQuery('')
  }, [searchOpen])

  /** Check whether a nav link is "active" based on the current pathname. */
  const isActive = useCallback(
    (href: string) => {
      if (href === '/') return pathname === '/'
      return pathname.startsWith(href)
    },
    [pathname],
  )

  /** Derive user initial for the avatar circle. */
  const userInitial: string = (() => {
    if (user?.first_name) return user.first_name.charAt(0).toUpperCase()
    if (user?.name) return user.name.charAt(0).toUpperCase()
    if (user?.email) return user.email.charAt(0).toUpperCase()
    return 'U'
  })()

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <>
      {/* ── Header bar ─────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-[1000] flex h-[72px] items-center transition-all duration-300 ${
          scrolled
            ? 'border-b border-border bg-[rgba(5,5,5,0.97)] backdrop-blur-[16px]'
            : 'border-b border-transparent bg-[linear-gradient(180deg,rgba(5,5,5,0.98)_0%,rgba(5,5,5,0)_100%)]'
        }`}
      >
        <div className="flex w-full items-center justify-between px-4 sm:px-6 lg:px-10">
          {/* ── LEFT: Logo + mobile hamburger ──────────────────────── */}
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="group flex flex-col justify-center gap-[5px] md:hidden"
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
            >
              <span
                className={`block h-[2px] w-[22px] rounded-full bg-white/65 transition-all duration-300 group-hover:bg-primary ${
                  mobileMenuOpen
                    ? 'translate-y-[7px] rotate-45'
                    : ''
                }`}
              />
              <span
                className={`block h-[2px] w-[22px] rounded-full bg-white/65 transition-all duration-300 group-hover:bg-primary ${
                  mobileMenuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block h-[2px] w-[22px] rounded-full bg-white/65 transition-all duration-300 group-hover:bg-primary ${
                  mobileMenuOpen
                    ? '-translate-y-[7px] -rotate-45'
                    : ''
                }`}
              />
            </button>

            {/* Logo */}
            <Link href="/" className="flex-shrink-0" aria-label="Deep Pockets home">
              <Image
                src="/logo.webp"
                alt="Deep Pockets"
                width={180}
                height={36}
                className="h-9 w-auto"
                sizes="180px"
                priority
              />
            </Link>
          </div>

          {/* ── CENTER: Nav links (hidden on mobile) ──────────────── */}
          <nav
            className="hidden items-center gap-6 md:flex lg:gap-8"
            aria-label="Main navigation"
          >
            {NAV_LINKS.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-condensed text-[13px] font-bold uppercase tracking-[2px] text-white/65 transition-colors duration-200 hover:text-white"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative font-condensed text-[13px] font-bold uppercase tracking-[2px] transition-colors duration-200 ${
                    isActive(link.href)
                      ? 'text-white after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:rounded-full after:bg-primary after:content-[""]'
                      : 'text-white/65 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ),
            )}

            {/* Studio link – admin only */}
            {userIsAdmin && (
              <Link
                href="/studio"
                className={`relative font-condensed text-[13px] font-bold uppercase tracking-[2px] transition-colors duration-200 ${
                  isActive('/studio')
                    ? 'text-primary after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:rounded-full after:bg-primary after:content-[""]'
                    : 'text-primary/80 hover:text-primary'
                }`}
              >
                Studio
              </Link>
            )}
          </nav>

          {/* ── RIGHT: Search, bell, avatar ────────────────────────── */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Inline search */}
            <form
              onSubmit={handleSearchSubmit}
              className="flex items-center"
            >
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  searchOpen
                    ? 'mr-1 w-[160px] opacity-100 sm:w-[200px]'
                    : 'w-0 opacity-0'
                }`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search…"
                  className="h-8 w-full rounded border border-border bg-white/[0.07] px-3 font-condensed text-[13px] tracking-[1px] text-white placeholder-white/40 outline-none transition-colors focus:border-primary"
                  aria-label="Search videos"
                />
              </div>

              {/* Search toggle button */}
              <button
                type={searchOpen && searchQuery.trim() ? 'submit' : 'button'}
                onClick={
                  searchOpen && searchQuery.trim() ? undefined : toggleSearch
                }
                className="flex h-9 w-9 items-center justify-center rounded-full text-white/65 transition-colors duration-200 hover:text-primary"
                aria-label={searchOpen ? 'Submit search' : 'Open search'}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </form>

            {/* Notification bell */}
            <button
              className="flex h-9 w-9 items-center justify-center rounded-full text-white/65 transition-colors duration-200 hover:text-primary"
              aria-label="Notifications"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </button>

            {/* User avatar / dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold-dim text-[14px] font-bold text-white transition-shadow duration-200 hover:ring-2 hover:ring-primary/50"
                aria-label="User menu"
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
              >
                {userInitial}
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-border bg-secondary shadow-xl shadow-black/40">
                  {isAuthenticated && user ? (
                    <>
                      <div className="border-b border-border px-4 py-3">
                        <p className="text-xs text-muted-foreground">
                          Signed in as
                        </p>
                        <p className="truncate text-sm font-medium text-white">
                          {user.email}
                        </p>
                      </div>

                      <Link
                        href="/profile"
                        className="block px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-white"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Your Profile
                      </Link>
                      <Link
                        href="/settings"
                        className="block px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-white"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Settings
                      </Link>

                      <hr className="my-1 border-border" />

                      <button
                        onClick={handleLogout}
                        className="block w-full px-4 py-2.5 text-left text-sm text-red-400 transition-colors hover:bg-white/[0.05]"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        href="/login"
                        className="block px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-white"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Sign In
                      </Link>
                      <Link
                        href="/register"
                        className="block px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-white"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Mobile menu ────────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <nav
          className="fixed left-0 right-0 top-[72px] z-[999] flex flex-col gap-5 border-b border-border bg-[rgba(5,5,5,0.98)] px-8 py-6 backdrop-blur-[16px] md:hidden"
          aria-label="Mobile navigation"
        >
          {NAV_LINKS.map((link) =>
            link.external ? (
              <a
                key={link.href}
                href={link.href}
                className="font-condensed text-[18px] font-bold uppercase tracking-[2px] text-white/65 transition-colors duration-200 hover:text-primary"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                href={link.href}
                className={`font-condensed text-[18px] font-bold uppercase tracking-[2px] transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-primary'
                    : 'text-white/65 hover:text-primary'
                }`}
              >
                {link.label}
              </Link>
            ),
          )}

          {/* Studio link – admin only (mobile) */}
          {userIsAdmin && (
            <Link
              href="/studio"
              className={`font-condensed text-[18px] font-bold uppercase tracking-[2px] transition-colors duration-200 ${
                isActive('/studio')
                  ? 'text-primary'
                  : 'text-primary/80 hover:text-primary'
              }`}
            >
              Studio
            </Link>
          )}
        </nav>
      )}
    </>
  )
}
